import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, ChevronRight, ChevronLeft, Sparkles, User, MessageSquare, 
  Trophy, Calendar, Code2, Zap, Check, Rocket
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';
import { Button } from './ui';
import Confetti from './Confetti';

interface Step {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  action?: string;
  actionPath?: string;
  points: number;
}

const TOUR_STEPS: Step[] = [
  {
    id: 'welcome',
    title: 'Welcome to AUDC! 🎉',
    description: 'You\'re now part of Ambo University Developers Club. Let\'s take a quick tour to help you get started.',
    icon: <Sparkles className="w-8 h-8" />,
    points: 0,
  },
  {
    id: 'profile',
    title: 'Complete Your Profile',
    description: 'Add a bio, your skills, and connect your GitHub/Telegram to earn points and get verified.',
    icon: <User className="w-8 h-8" />,
    action: 'Go to Profile',
    actionPath: '/settings',
    points: 10,
  },
  {
    id: 'feed',
    title: 'Join the Conversation',
    description: 'Share your thoughts, projects, and questions. Upvote posts you like and comment to earn points!',
    icon: <MessageSquare className="w-8 h-8" />,
    action: 'View Feed',
    actionPath: '/feed',
    points: 5,
  },
  {
    id: 'leaderboard',
    title: 'Climb the Leaderboard',
    description: 'Earn points by being active. Top contributors get featured and earn special badges!',
    icon: <Trophy className="w-8 h-8" />,
    action: 'See Leaderboard',
    actionPath: '/leaderboard',
    points: 0,
  },
  {
    id: 'events',
    title: 'Join Events & Hackathons',
    description: 'RSVP to events, attend workshops, and participate in hackathons to level up your skills.',
    icon: <Calendar className="w-8 h-8" />,
    action: 'Browse Events',
    actionPath: '/events',
    points: 5,
  },
  {
    id: 'projects',
    title: 'Showcase Your Work',
    description: 'Share your projects with the community. Get feedback, find collaborators, and inspire others!',
    icon: <Code2 className="w-8 h-8" />,
    action: 'View Projects',
    actionPath: '/projects',
    points: 10,
  },
  {
    id: 'complete',
    title: 'You\'re All Set! 🚀',
    description: 'You\'ve earned bonus points for completing the tour. Now go build something amazing!',
    icon: <Rocket className="w-8 h-8" />,
    points: 25,
  },
];

export function OnboardingTour() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const { user, profile } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is new and hasn't completed onboarding
    if (user && profile && !profile.onboarding_completed) {
      // Small delay to let the page load
      const timer = setTimeout(() => setIsOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user, profile]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = async () => {
    await markComplete();
    setIsOpen(false);
  };

  const handleComplete = async () => {
    setShowConfetti(true);
    await markComplete();
    
    // Add bonus points for completing tour
    if (user) {
      await supabase.rpc('add_points', {
        user_id_param: user.id,
        points_param: 25,
        reason_param: 'Completed onboarding tour'
      });
    }

    setTimeout(() => {
      setShowConfetti(false);
      setIsOpen(false);
    }, 3000);
  };

  const markComplete = async () => {
    if (user) {
      await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('id', user.id);
    }
  };

  const handleAction = (path?: string) => {
    if (path) {
      setIsOpen(false);
      navigate(path);
    }
  };

  if (!isOpen) return null;

  const step = TOUR_STEPS[currentStep];
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  return (
    <>
      {showConfetti && <Confetti />}
      
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
        
        {/* Modal */}
        <div className="relative w-full max-w-md bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
          {/* Progress bar */}
          <div className="h-1 bg-slate-800">
            <div 
              className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Close button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>

          {/* Content */}
          <div className="p-8 text-center">
            {/* Icon */}
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 flex items-center justify-center mx-auto mb-6 text-green-400">
              {step.icon}
            </div>

            {/* Step indicator */}
            <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">
              Step {currentStep + 1} of {TOUR_STEPS.length}
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-white mb-3 tracking-tight">
              {step.title}
            </h2>

            {/* Description */}
            <p className="text-slate-400 leading-relaxed mb-6">
              {step.description}
            </p>

            {/* Points badge */}
            {step.points > 0 && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 text-sm font-medium mb-6">
                <Zap size={14} />
                +{step.points} points
              </div>
            )}

            {/* Action button */}
            {step.action && (
              <div className="mb-6">
                <Button
                  variant="secondary"
                  onClick={() => handleAction(step.actionPath)}
                  className="w-full"
                >
                  {step.action}
                </Button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 pb-8 flex items-center justify-between">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                currentStep === 0
                  ? 'text-slate-600 cursor-not-allowed'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <ChevronLeft size={16} />
              Back
            </button>

            <div className="flex gap-1.5">
              {TOUR_STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === currentStep
                      ? 'bg-green-500'
                      : i < currentStep
                      ? 'bg-green-500/50'
                      : 'bg-slate-700'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              className="flex items-center gap-1 px-4 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-black text-sm font-bold transition-colors"
            >
              {currentStep === TOUR_STEPS.length - 1 ? (
                <>
                  <Check size={16} />
                  Done
                </>
              ) : (
                <>
                  Next
                  <ChevronRight size={16} />
                </>
              )}
            </button>
          </div>

          {/* Skip link */}
          <div className="pb-6 text-center">
            <button
              onClick={handleSkip}
              className="text-sm text-slate-500 hover:text-slate-400"
            >
              Skip tour
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default OnboardingTour;


